export const consoleRoutePaths = {
  control: '/control',
  investigations: '/devices/:deviceId/anomalies',
  onboard: '/devices/:deviceId/setup',
  'browser-mic-stream': '/browser-mic-stream',
  'reset-usb': '/recovery/reset-usb',
  training: '/admin/power/training',
  models: '/admin/power/models',
  devices: '/devices',
  users: '/admin/users',
  'surveillance-policies': '/admin/surveillance',
  radio: '/admin/radio',
  'file-detection': '/admin/power/advanced/file-detection',
  'owner-users': '/owner/users',
  'owner-organizations': '/owner/organizations',
  'owner-system-health': '/owner/system-health',
  'owner-audit-log': '/owner/audit-log',
  'owner-acoustic-domains': '/owner/acoustic-domains',
  profile: '/profile',
} as const;

export const consoleRoutePatterns = {
  login: '/login',
  root: '/',
  catchAll: '*',
  ownerBase: '/owner',
  ownerRoot: '/owner/*',
  ownerUsersChild: 'users',
  ownerOrganizationsChild: 'organizations',
  ownerSystemHealthChild: 'system-health',
  ownerAuditLogChild: 'audit-log',
  ownerAcousticDomainsChild: 'acoustic-domains',
  trainingSummary: '/admin/power/training/summary/:batchId',
  deviceWorkspace: '/devices/:deviceId',
} as const;

export type DeviceWorkspaceSection =
  | 'live'
  | 'evolution'
  | 'guard'
  | 'recordings'
  | 'pipeline'
  | 'settings';

export function getDeviceWorkspacePath(
  deviceId: number,
  section?: DeviceWorkspaceSection,
): string {
  requireDeviceId(deviceId);
  const path = `${consoleRoutePaths.devices}/${deviceId}`;
  return section ? `${path}?section=${section}` : path;
}

/** Element id of the "Improve detection" card on a device's Models tab. */
export const DEVICE_IMPROVE_DETECTION_FRAGMENT = 'improve-detection';

/** The device's Models tab, scrolled to "Improve detection". */
export function getDeviceImproveDetectionPath(deviceId: number): string {
  return `${getDeviceWorkspacePath(deviceId, 'pipeline')}#${DEVICE_IMPROVE_DETECTION_FRAGMENT}`;
}

function requireDeviceId(deviceId: number): void {
  if (!Number.isSafeInteger(deviceId) || deviceId <= 0) {
    throw new Error('Device paths require a positive integer device ID.');
  }
}

export function getDeviceSetupPath(
  deviceId: number,
  query: Readonly<Record<string, string>> = {},
): string {
  requireDeviceId(deviceId);
  const path = `${consoleRoutePaths.devices}/${deviceId}/setup`;
  const params = new URLSearchParams(query);
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

export function getDeviceAnomaliesPath(
  deviceId: number,
  detectionResultId?: number,
): string {
  requireDeviceId(deviceId);
  if (detectionResultId === undefined) {
    return `${consoleRoutePaths.devices}/${deviceId}/anomalies`;
  }
  if (!Number.isSafeInteger(detectionResultId) || detectionResultId <= 0) {
    throw new Error('Anomaly paths require a positive integer detection ID.');
  }
  const params = new URLSearchParams({ detection: String(detectionResultId) });
  return `${consoleRoutePaths.devices}/${deviceId}/anomalies?${params.toString()}`;
}

/** What the Training wizard opens with: a saved dataset or a capture session. */
export type TrainingWizardSource =
  | { datasetId: string }
  | { captureSessionId: number };

/** The Training page with its wizard open on the given dataset or capture. */
export function getTrainingWizardPath(source: TrainingWizardSource): string {
  const params = new URLSearchParams({ openWizard: 'true' });
  if ('datasetId' in source) {
    params.set('datasetId', source.datasetId);
  } else {
    params.set('captureSessionId', String(source.captureSessionId));
  }
  return `${consoleRoutePaths.training}?${params.toString()}`;
}

/** The summary page of one training batch. */
export function getTrainingSummaryPath(batchId: string): string {
  if (!batchId) {
    throw new Error('Training summary paths require a batch ID.');
  }
  return consoleRoutePatterns.trainingSummary.replace(
    ':batchId',
    encodeURIComponent(batchId),
  );
}

export function parseDeviceWorkspaceId(value?: string): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const deviceId = Number(value);
  return Number.isSafeInteger(deviceId) && deviceId > 0 ? deviceId : null;
}

export type ConsoleRouteId = keyof typeof consoleRoutePaths;
export type ConsoleRoutePath = (typeof consoleRoutePaths)[ConsoleRouteId];

export function getConsoleRoutePath(id: ConsoleRouteId): ConsoleRoutePath {
  return consoleRoutePaths[id];
}
